import { Fragment, useRef, type MutableRefObject, type RefObject } from 'react';
import { ChevronRight, FileText, Network, Pencil, Users, Zap, TriangleAlert } from 'lucide-react';
import type { SowPoolUsageDisplayMetrics } from '@wevo/wevo-access/sow-credit-pools';

import { cn } from '../../../lib/utils';
import type {
    AdminCompanyAdminStatus,
    AdminPulseSubscription,
    AdminUserRow,
} from '../../../services/api';
import { Card } from '../../ui/card';
import { LoadingState } from '../../LoadingSpinner';
import { InlineAlert } from '../../ui/inline-alert';
import { Spinner } from '../../ui/spinner';
import { Input } from '../../ui/input';
import { SearchField } from '../../ui/search-field';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    getClickableRowProps,
} from '../../ui/table';
import { AgreementStatusBadge } from './AgreementStatusBadge';
import { isImmediateSearchKey } from '../adminDashboardHelpers';
import { AddUsersViaUsersPageNote } from './CompanyDetailPrimitives';
import { CompanyUsersPaginatedSection } from './CompanyUsersPaginatedSection';
import { SowTableUsageMetrics } from './SowUsageDisplay.tsx';
import { formatCredits, formatEnumLabel } from './adminCreditAdapters';
import { getPulseSubscriptionStatus } from './pulseSubscriptionStatus';
import type { CompanyDetailTab } from './companyDetailTabs';
import { TabList } from '../../ui/tabs';
import { SegmentedTab } from '../../ui/segmented-control';
import { segmentedTabListClassName } from '../../ui/segmented-styles';

type CompanySowRowLike = {
    id: string;
    name: string;
    startDate: string;
    endDate?: string | null;
    state: string;
    available: number;
    proUsage: SowPoolUsageDisplayMetrics | null;
    pulseUsage: SowPoolUsageDisplayMetrics | null;
};

type CompanyTeamRow = {
    id: string;
    name: string;
    isActive?: boolean;
    memberCount?: number;
    activeMemberCount?: number;
    members: Array<{
        user: {
            id: string | number;
            name: string;
            email: string;
        };
        membershipActive?: boolean;
    }>;
};

type InlineTeamEdit = {
    teamId: number;
    draft: string;
    originalName: string;
    error?: string;
} | null;

export function CompanyDetailTabBar({
    activeTab,
    sowCount,
    pulseSubscriptionCount,
    teamCount,
    userCount,
    onSelectTab,
}: {
    activeTab: CompanyDetailTab;
    sowCount: number | undefined;
    pulseSubscriptionCount: number | undefined;
    teamCount: number | undefined;
    userCount: number | undefined;
    onSelectTab: (tab: CompanyDetailTab) => void;
}) {
    return (
        <TabList label="Company sections" className={segmentedTabListClassName}>
            <SegmentedTab
                id="company-tab-sows"
                panelId="company-detail-panel"
                selected={activeTab === 'sows'}
                onSelect={() => onSelectTab('sows')}
                icon={FileText}
                label={sowCount === undefined ? 'SOWs' : `SOWs (${sowCount})`}
            />
            <SegmentedTab
                id="company-tab-teams"
                panelId="company-detail-panel"
                selected={activeTab === 'teams'}
                onSelect={() => onSelectTab('teams')}
                icon={Network}
                label={teamCount === undefined ? 'Teams' : `Teams (${teamCount})`}
            />
            <SegmentedTab
                id="company-tab-users"
                panelId="company-detail-panel"
                selected={activeTab === 'users'}
                onSelect={() => onSelectTab('users')}
                icon={Users}
                label={userCount === undefined ? 'Users' : `Users (${userCount})`}
            />
            <SegmentedTab
                id="company-tab-pulse-subscriptions"
                panelId="company-detail-panel"
                selected={activeTab === 'pulse-subscriptions'}
                onSelect={() => onSelectTab('pulse-subscriptions')}
                icon={Zap}
                label={
                    pulseSubscriptionCount === undefined
                        ? 'Pulse subscriptions'
                        : `Pulse subscriptions (${pulseSubscriptionCount})`
                }
            />
        </TabList>
    );
}

export function CompanyPulseSubscriptionsTab({
    subscriptions,
    isPending,
    isError,
    onRetry,
    isRetrying,
    onRowClick,
}: {
    subscriptions: AdminPulseSubscription[];
    isPending: boolean;
    isError: boolean;
    onRetry?: () => void;
    isRetrying?: boolean;
    onRowClick: (subscription: AdminPulseSubscription) => void;
}) {
    if (isError) {
        return (
            <InlineAlert
                tone="error"
                title="Couldn't load Pulse subscriptions"
                onRetry={onRetry}
                isRetrying={isRetrying}
            />
        );
    }
    if (isPending) {
        return (
            <Card>
                <LoadingState message="Loading Pulse subscriptions…" />
            </Card>
        );
    }
    return (
        <Card className="overflow-hidden">
            <Table aria-label="Pulse subscriptions">
                <TableHeader>
                    <TableRow>
                        <TableHead>Subscription ID</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Scope</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Started</TableHead>
                        <TableHead>Expires</TableHead>
                        <TableHead className="text-right">Total credits</TableHead>
                        <TableHead className="text-right">Seats</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {subscriptions.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={8} className="py-8 text-center text-neutral-500">
                                No Pulse subscriptions for this company.
                            </TableCell>
                        </TableRow>
                    ) : (
                        subscriptions.map((subscription) => (
                            <TableRow
                                key={subscription.id}
                                className="cursor-pointer"
                                {...getClickableRowProps(() => onRowClick(subscription))}
                            >
                                <TableCell className="tabular-nums text-neutral-700">
                                    {subscription.id}
                                </TableCell>
                                <TableCell>
                                    <AgreementStatusBadge
                                        status={getPulseSubscriptionStatus(
                                            subscription.startedAt,
                                            subscription.expiresAt,
                                        )}
                                    />
                                </TableCell>
                                <TableCell className="min-w-[10rem]">
                                    <p className="font-medium">
                                        {formatEnumLabel(subscription.scope)}
                                    </p>
                                    <p className="text-xs text-neutral-500">
                                        {subscription.scope === 'company'
                                            ? `Company #${subscription.companyId}`
                                            : `${subscription.teamName ?? 'Team'} #${
                                                  subscription.teamId
                                              }`}
                                        {subscription.sowId ? ` · SOW ${subscription.sowId}` : ''}
                                    </p>
                                </TableCell>
                                <TableCell>
                                    {subscription.tierType === 'enterprise_placeholder'
                                        ? 'Enterprise'
                                        : (subscription.tierName ?? subscription.tierType ?? '—')}
                                </TableCell>
                                <TableCell>
                                    {subscription.startedAt
                                        ? new Date(subscription.startedAt).toLocaleString()
                                        : '—'}
                                </TableCell>
                                <TableCell>
                                    {subscription.expiresAt
                                        ? new Date(subscription.expiresAt).toLocaleString()
                                        : 'Open'}
                                </TableCell>
                                <TableCell className="text-right tabular-nums">
                                    {subscription.totalCredits === null
                                        ? '—'
                                        : formatCredits(subscription.totalCredits)}
                                </TableCell>
                                <TableCell className="text-right tabular-nums">
                                    {subscription.numSeats}
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </Card>
    );
}

export function CompanySowsTab({
    rows,
    isLoading,
    isError,
    onRetry,
    isRetrying,
    onRowClick,
}: {
    rows: CompanySowRowLike[];
    isLoading: boolean;
    isError: boolean;
    onRetry?: () => void;
    isRetrying?: boolean;
    onRowClick: (sow: CompanySowRowLike) => void;
}) {
    if (isError) {
        return (
            <InlineAlert
                tone="error"
                title="Couldn't load SOWs"
                onRetry={onRetry}
                isRetrying={isRetrying}
            />
        );
    }
    if (isLoading) {
        return (
            <Card>
                <LoadingState message="Loading SOWs…" />
            </Card>
        );
    }
    return (
        <Card className="overflow-clip">
            <Table stickyHeader aria-label="SOWs">
                <TableHeader>
                    <TableRow>
                        <TableHead>SOW</TableHead>
                        <TableHead>Period</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Pro usage</TableHead>
                        <TableHead className="text-right">Pulse usage</TableHead>
                        <TableHead className="text-right">Available</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={6} className="py-8 text-center text-neutral-500">
                                No SOWs for this company.
                            </TableCell>
                        </TableRow>
                    ) : (
                        rows.map((sow) => (
                            <TableRow
                                key={sow.id}
                                className="cursor-pointer"
                                {...getClickableRowProps(() => onRowClick(sow))}
                            >
                                <TableCell className="min-w-[12rem]">
                                    <p className="font-medium text-neutral-950">{sow.name}</p>
                                    <p className="text-xs text-neutral-500">{sow.id}</p>
                                </TableCell>
                                <TableCell className="whitespace-nowrap">
                                    {new Date(sow.startDate).toLocaleDateString()} -{' '}
                                    {sow.endDate
                                        ? new Date(sow.endDate).toLocaleDateString()
                                        : 'Open'}
                                </TableCell>
                                <TableCell>
                                    <AgreementStatusBadge status={sow.state} />
                                </TableCell>
                                <TableCell className="text-right">
                                    {sow.proUsage ? (
                                        <SowTableUsageMetrics
                                            used={sow.proUsage.used}
                                            granted={sow.proUsage.granted}
                                            usageCommitted={sow.proUsage.usageCommitted}
                                            usagePending={sow.proUsage.usagePending}
                                            usagePercent={sow.proUsage.usagePercent}
                                            committedUsagePercent={
                                                sow.proUsage.committedUsagePercent
                                            }
                                            pendingUsagePercent={sow.proUsage.pendingUsagePercent}
                                        />
                                    ) : (
                                        <span className="text-neutral-500">-</span>
                                    )}
                                </TableCell>
                                <TableCell className="text-right">
                                    {sow.pulseUsage ? (
                                        <SowTableUsageMetrics
                                            used={sow.pulseUsage.used}
                                            granted={sow.pulseUsage.granted}
                                            usageCommitted={sow.pulseUsage.usageCommitted}
                                            usagePending={sow.pulseUsage.usagePending}
                                            usagePercent={sow.pulseUsage.usagePercent}
                                            committedUsagePercent={
                                                sow.pulseUsage.committedUsagePercent
                                            }
                                            pendingUsagePercent={sow.pulseUsage.pendingUsagePercent}
                                        />
                                    ) : (
                                        <span className="text-neutral-500">-</span>
                                    )}
                                </TableCell>
                                <TableCell
                                    className={cn(
                                        'text-right tabular-nums font-medium',
                                        sow.available < 0 ? 'text-amber-800' : 'text-neutral-950',
                                    )}
                                >
                                    {sow.available < 0 ? (
                                        <TriangleAlert
                                            aria-hidden
                                            className="mr-1 inline size-3.5 text-amber-600"
                                        />
                                    ) : null}
                                    {formatCredits(sow.available)}
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </Card>
    );
}

function TeamNameCell({
    team,
    teamNumericId,
    inlineTeamEdit,
    inputRef,
    cancelledRef,
    disabled,
    onDraftChange,
    onFinish,
    onCancel,
    onStartEdit,
}: {
    team: CompanyTeamRow;
    teamNumericId: number;
    inlineTeamEdit: InlineTeamEdit;
    inputRef: RefObject<HTMLInputElement>;
    cancelledRef: MutableRefObject<boolean>;
    disabled: boolean;
    onDraftChange: (teamId: number, value: string) => void;
    onFinish: () => Promise<void>;
    onCancel: () => void;
    onStartEdit: (teamId: number, name: string) => void;
}) {
    return inlineTeamEdit?.teamId === teamNumericId ? (
        <div>
            <Input
                ref={inputRef}
                className="inline-flex h-8 max-w-[min(100%,20rem)] font-medium"
                value={inlineTeamEdit.draft}
                onChange={(event) => onDraftChange(teamNumericId, event.target.value)}
                onBlur={() => {
                    if (cancelledRef.current) {
                        cancelledRef.current = false;
                        return;
                    }
                    void onFinish();
                }}
                onKeyDown={(event) => {
                    if (event.key === 'Escape') {
                        event.preventDefault();
                        event.stopPropagation();
                        cancelledRef.current = true;
                        onCancel();
                        requestAnimationFrame(() => {
                            document
                                .querySelector<HTMLButtonElement>(
                                    `[data-team-name-edit="${teamNumericId}"]`,
                                )
                                ?.focus();
                            cancelledRef.current = false;
                        });
                    } else if (event.key === 'Enter') {
                        event.preventDefault();
                        event.currentTarget.blur();
                    }
                }}
                autoComplete="off"
                aria-label={`Edit ${team.name} team name`}
                aria-invalid={inlineTeamEdit.error ? true : undefined}
                aria-describedby={
                    inlineTeamEdit.error ? `team-name-error-${teamNumericId}` : undefined
                }
                disabled={disabled}
            />
            {inlineTeamEdit.error ? (
                <p
                    id={`team-name-error-${teamNumericId}`}
                    role="alert"
                    className="mt-1 text-xs text-destructive"
                >
                    {inlineTeamEdit.error}
                </p>
            ) : null}
        </div>
    ) : (
        <>
            <span className="font-medium">{team.name}</span>
            <button
                type="button"
                data-team-name-edit={teamNumericId}
                className="ml-2 inline-flex size-7 items-center justify-center rounded-md text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900"
                aria-label={`Edit ${team.name} team name`}
                disabled={!Number.isFinite(teamNumericId) || disabled}
                onClick={(event) => {
                    event.stopPropagation();
                    if (!Number.isFinite(teamNumericId)) {
                        return;
                    }
                    onStartEdit(teamNumericId, team.name);
                }}
            >
                <Pencil className="size-4" aria-hidden />
            </button>
        </>
    );
}

export function CompanyTeamsTab({
    teams,
    isLoading,
    isError,
    onRetry,
    isRetrying,
    expandedTeamIds,
    inlineTeamEdit,
    inlineTeamNameInputRef,
    onToggleExpanded,
    onStartInlineEdit,
    onInlineTeamDraftChange,
    onFinishInlineTeamNameEdit,
    onCancelInlineTeamEdit,
    onSetTeamActive,
    onSetMembershipActive,
    teamActivePendingTeamId,
    teamMembershipLocked,
    teamMembershipPending,
}: {
    teams: CompanyTeamRow[];
    isLoading: boolean;
    isError: boolean;
    onRetry?: () => void;
    isRetrying?: boolean;
    expandedTeamIds: Set<string>;
    inlineTeamEdit: InlineTeamEdit;
    inlineTeamNameInputRef: RefObject<HTMLInputElement>;
    onToggleExpanded: (teamId: string) => void;
    onStartInlineEdit: (teamId: number, name: string) => void;
    onInlineTeamDraftChange: (teamId: number, value: string) => void;
    onFinishInlineTeamNameEdit: () => Promise<void>;
    onCancelInlineTeamEdit: () => void;
    onSetTeamActive: (teamId: number, isActive: boolean) => void;
    onSetMembershipActive: (teamId: number, userId: number, isActive: boolean) => void;
    teamActivePendingTeamId: number | null;
    teamMembershipLocked: boolean;
    teamMembershipPending: { teamId: number; userId: number } | null;
}) {
    const cancelledRef = useRef(false);

    if (isError) {
        return (
            <InlineAlert
                tone="error"
                title="Couldn't load teams"
                onRetry={onRetry}
                isRetrying={isRetrying}
            />
        );
    }
    if (isLoading) {
        return (
            <Card>
                <LoadingState message="Loading teams…" />
            </Card>
        );
    }
    return (
        <>
            <AddUsersViaUsersPageNote />
            <Card className="overflow-clip">
                <Table stickyHeader aria-label="Teams">
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-10">
                                <span className="sr-only">Expand</span>
                            </TableHead>
                            <TableHead>Team</TableHead>
                            <TableHead className="text-right">Active members</TableHead>
                            <TableHead className="w-28 text-right">Active</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {teams.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={4}
                                    className="py-8 text-center text-neutral-500"
                                >
                                    No teams for this company.
                                </TableCell>
                            </TableRow>
                        ) : (
                            teams.map((team) => {
                                const teamNumericId = Number(team.id);
                                const teamIsActive = team.isActive !== false;
                                const expanded = expandedTeamIds.has(team.id);
                                const activeMembers =
                                    team.activeMemberCount ?? team.memberCount ?? 0;
                                return (
                                    <Fragment key={team.id}>
                                        <TableRow className="align-middle">
                                            <TableCell className="w-10">
                                                <button
                                                    type="button"
                                                    className="flex size-8 items-center justify-center rounded-md text-neutral-600 hover:bg-neutral-100"
                                                    aria-expanded={expanded}
                                                    aria-label={
                                                        expanded
                                                            ? 'Hide team members'
                                                            : 'Show team members'
                                                    }
                                                    onClick={() => onToggleExpanded(team.id)}
                                                >
                                                    <ChevronRight
                                                        className={`size-4 transition-transform ${
                                                            expanded ? 'rotate-90' : ''
                                                        }`}
                                                    />
                                                </button>
                                            </TableCell>
                                            <TableCell
                                                className={
                                                    teamIsActive
                                                        ? 'text-neutral-950'
                                                        : 'text-neutral-500'
                                                }
                                            >
                                                <TeamNameCell
                                                    team={team}
                                                    teamNumericId={teamNumericId}
                                                    inlineTeamEdit={inlineTeamEdit}
                                                    inputRef={inlineTeamNameInputRef}
                                                    cancelledRef={cancelledRef}
                                                    disabled={
                                                        teamActivePendingTeamId === teamNumericId
                                                    }
                                                    onDraftChange={onInlineTeamDraftChange}
                                                    onFinish={onFinishInlineTeamNameEdit}
                                                    onCancel={onCancelInlineTeamEdit}
                                                    onStartEdit={onStartInlineEdit}
                                                />
                                                <span className="ml-1.5 font-normal text-neutral-500">
                                                    (#{team.id})
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-right tabular-nums">
                                                {activeMembers}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <label className="inline-flex size-9 cursor-pointer items-center justify-end">
                                                    <input
                                                        type="checkbox"
                                                        aria-label={`Active: ${team.name}`}
                                                        className="size-4 rounded border-neutral-300"
                                                        checked={teamIsActive}
                                                        disabled={
                                                            !Number.isFinite(teamNumericId) ||
                                                            teamActivePendingTeamId ===
                                                                teamNumericId
                                                        }
                                                        onChange={(event) => {
                                                            if (!Number.isFinite(teamNumericId)) {
                                                                return;
                                                            }
                                                            onSetTeamActive(
                                                                teamNumericId,
                                                                event.target.checked,
                                                            );
                                                        }}
                                                    />
                                                </label>
                                            </TableCell>
                                        </TableRow>
                                        {expanded ? (
                                            <TableRow
                                                key={`${team.id}-members`}
                                                className="bg-neutral-50 hover:bg-neutral-50"
                                            >
                                                <TableCell
                                                    colSpan={4}
                                                    className="p-0 first:pl-0 last:pr-0 sm:first:pl-0 sm:last:pr-0"
                                                >
                                                    <div className="py-3 pl-[5.5rem] pr-4 sm:pl-[5.75rem] sm:pr-5">
                                                        {team.members.length === 0 ? (
                                                            <p className="text-sm text-neutral-500">
                                                                No member assignments for this team
                                                                yet.
                                                            </p>
                                                        ) : (
                                                            <ul className="divide-y divide-neutral-200/80">
                                                                {team.members.map((member) => {
                                                                    const userNumericId = Number(
                                                                        member.user.id,
                                                                    );
                                                                    const membershipOn =
                                                                        member.membershipActive !==
                                                                        false;
                                                                    const membershipBusy =
                                                                        teamMembershipPending?.teamId ===
                                                                            teamNumericId &&
                                                                        teamMembershipPending?.userId ===
                                                                            userNumericId;
                                                                    return (
                                                                        <li
                                                                            key={member.user.id}
                                                                            className="grid grid-cols-1 items-center gap-x-4 gap-y-1.5 py-2.5 first:pt-0 last:pb-0 sm:grid-cols-[minmax(0,1fr)_auto]"
                                                                        >
                                                                            <div className="min-w-0">
                                                                                <p className="font-medium text-neutral-950">
                                                                                    {
                                                                                        member.user
                                                                                            .name
                                                                                    }
                                                                                </p>
                                                                                <p className="mt-0.5 text-sm text-neutral-500 [overflow-wrap:anywhere]">
                                                                                    {
                                                                                        member.user
                                                                                            .email
                                                                                    }
                                                                                    <span className="ml-1.5 text-neutral-500">
                                                                                        (#
                                                                                        {
                                                                                            member
                                                                                                .user
                                                                                                .id
                                                                                        }
                                                                                        )
                                                                                    </span>
                                                                                </p>
                                                                            </div>
                                                                            <label className="flex shrink-0 cursor-pointer items-center gap-1.5 text-sm text-neutral-700">
                                                                                <span>
                                                                                    Active
                                                                                    membership
                                                                                </span>
                                                                                <input
                                                                                    type="checkbox"
                                                                                    aria-label={`Active membership for ${member.user.name}`}
                                                                                    className="size-4 rounded border-neutral-300"
                                                                                    checked={
                                                                                        membershipOn
                                                                                    }
                                                                                    disabled={
                                                                                        !Number.isFinite(
                                                                                            teamNumericId,
                                                                                        ) ||
                                                                                        !Number.isFinite(
                                                                                            userNumericId,
                                                                                        ) ||
                                                                                        teamMembershipLocked
                                                                                    }
                                                                                    onChange={(
                                                                                        event,
                                                                                    ) => {
                                                                                        if (
                                                                                            !Number.isFinite(
                                                                                                teamNumericId,
                                                                                            ) ||
                                                                                            !Number.isFinite(
                                                                                                userNumericId,
                                                                                            )
                                                                                        ) {
                                                                                            return;
                                                                                        }
                                                                                        onSetMembershipActive(
                                                                                            teamNumericId,
                                                                                            userNumericId,
                                                                                            event
                                                                                                .target
                                                                                                .checked,
                                                                                        );
                                                                                    }}
                                                                                />
                                                                                {membershipBusy ? (
                                                                                    <span
                                                                                        role="status"
                                                                                        aria-label="Updating membership"
                                                                                        className="inline-flex items-center text-neutral-500"
                                                                                    >
                                                                                        <Spinner
                                                                                            size="xs"
                                                                                            label={
                                                                                                null
                                                                                            }
                                                                                            className="size-3 text-current"
                                                                                        />
                                                                                    </span>
                                                                                ) : null}
                                                                            </label>
                                                                        </li>
                                                                    );
                                                                })}
                                                            </ul>
                                                        )}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ) : null}
                                    </Fragment>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </Card>
        </>
    );
}

export function CompanyUsersTab({
    isError,
    isPending,
    onRetry,
    isRetrying,
    data,
    page,
    pageSize,
    searchDraft,
    searchQuery,
    onSearchChange,
    onSearchCommit,
    onPageChange,
    onRowClick,
    onSync,
    syncingUserId,
    companyAdminStatusByUserId,
    isCompanyAdminStatusPending,
    isCompanyAdminStatusError,
    activeCompanyMemberUserIds,
    companyAdminPendingUserId,
    onSetCompanyUserAdmin,
    organizationId,
}: {
    isError: boolean;
    isPending: boolean;
    onRetry?: () => void;
    isRetrying?: boolean;
    data:
        | {
              items: AdminUserRow[];
              total: number;
              totalPages: number;
          }
        | undefined;
    page: number;
    pageSize: number;
    searchDraft: string;
    searchQuery?: string;
    onSearchChange: (value: string) => void;
    onSearchCommit: (value?: string) => void;
    onPageChange: (page: number) => void;
    onRowClick: (user: AdminUserRow) => void;
    onSync: (userId: number) => void;
    syncingUserId: number | null;
    companyAdminStatusByUserId: Map<number, AdminCompanyAdminStatus>;
    isCompanyAdminStatusPending: boolean;
    isCompanyAdminStatusError: boolean;
    activeCompanyMemberUserIds: Set<number>;
    companyAdminPendingUserId: number | null;
    onSetCompanyUserAdmin: (userId: number, isAdmin: boolean) => void;
    organizationId: string | null;
}) {
    return (
        <>
            <SearchField
                variant="outlined"
                inputClassName="h-10 shadow-sm"
                value={searchDraft}
                onValueChange={onSearchChange}
                onClear={() => onSearchCommit('')}
                onKeyDown={(event) => {
                    if (isImmediateSearchKey(event.key)) {
                        onSearchCommit(event.currentTarget.value);
                    }
                }}
                placeholder="Search by user ID, name, or email"
                aria-label="Search company users"
            />
            <AddUsersViaUsersPageNote />
            {isError && (
                <InlineAlert
                    tone="error"
                    title="Couldn't load this company's users"
                    onRetry={onRetry}
                    isRetrying={isRetrying}
                >
                    Viewing users requires the administrate:users permission.
                </InlineAlert>
            )}
            {!isError && isPending && !data && (
                <Card>
                    <LoadingState message="Loading users…" />
                </Card>
            )}
            {!isError && data && (
                <CompanyUsersPaginatedSection
                    rows={data.items}
                    page={page}
                    pageSize={pageSize}
                    searchQuery={searchQuery}
                    total={data.total}
                    totalPages={data.totalPages}
                    onPageChange={onPageChange}
                    onRowClick={onRowClick}
                    onSync={onSync}
                    syncingUserId={syncingUserId}
                    companyAdminStatusByUserId={companyAdminStatusByUserId}
                    isCompanyAdminStatusPending={isCompanyAdminStatusPending}
                    isCompanyAdminStatusError={isCompanyAdminStatusError}
                    activeCompanyMemberUserIds={activeCompanyMemberUserIds}
                    companyAdminPendingUserId={companyAdminPendingUserId}
                    onSetCompanyUserAdmin={onSetCompanyUserAdmin}
                    organizationId={organizationId}
                />
            )}
        </>
    );
}
